import React from 'react';
import { FormattedMessage } from 'react-intl';

// Components
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import ProgramPassbookRate from '../ProgramPassbookRate';
import SetupGridItem from '../../../SetupGridItem';
import {
  AnnualRecertMonthPicker,
  SetupLabel,
  SetupSwitch,
  SetupValue,
} from '../shared';
import OnOffTypography from '../../../OnOffTypography';
import PropertyStateConfig from '../../../PropertyStateConfig';
import CertificationPastDue from '../../components/CertificationPastDue';
import RecertType from '../../components/RecertType';

import type { HTFValues, PropertyStateConfigValues } from '../../../types';

import { useHTF } from './hooks';
import messages from './messages';
import messagesAffordableSetup from '../../messages';

interface HTFFlags {
  passbookRate: boolean;
}

type HTFSetupValues = HTFValues & {
  flags: HTFFlags,
};

type Props = {
  values: HTFSetupValues,
} & PropertyStateConfigValues;

const HTFSetup = ({
  editMode,
  isLoadingPassbookRateData,
  onChange,
  promptToaster,
  values: { flags, passbookRates, hotmaConfig, ...values },
  state,
  stateConfigs,
}: Props) => {
  const { ReactHookFormProps, memoizedFields } = useHTF({
    editMode,
    onChange,
    values,
  });

  const handleHTFAnnualRecertMonthChange = (date: Object, validate: Object) => {
    onChange({
      target: { name: 'setup.HTFAnnualRecertMonth', value: date },
    });
  };

  return (
    <>
      <Form
        ReactHookFormProps={ReactHookFormProps}
        header={
          <Typography variant={'titleMedium'}>
            <FormattedMessage {...messages.title} />
          </Typography>
        }
        fields={memoizedFields}
      >
        <Grid container>
          <RecertType
            editMode={editMode}
            program="HTF"
            recertTypes={values.recertTypes}
            onChange={onChange}
          />
        </Grid>

        <Grid container spacing={2}>
          <SetupGridItem
            label={
              <FormattedMessage
                {...messagesAffordableSetup.autoRolloverIncomeAndAssets}
              />
            }
            TooltipProps={{
              title: (
                <FormattedMessage
                  {...messagesAffordableSetup.autoRolloverIncomeAndAssetsDescription}
                />
              ),
            }}
          >
            <SetupSwitch
              switchId={'home-auto-rollover-income-and-assets-switch'}
              editMode={editMode}
              name="setup.HTFAutoRolloverIncomeAndAssets"
              disabled={!editMode}
              value={values.autoRolloverIncomeAndAssets}
              onChange={onChange}
            />
          </SetupGridItem>
          <SetupGridItem
            label={
              <FormattedMessage
                {...messagesAffordableSetup.annualRecertMonth}
                values={{ program: 'HTF' }}
              />
            }
          >
            <AnnualRecertMonthPicker
              handleAnnualRecertMonthChange={handleHTFAnnualRecertMonthChange}
              editMode={editMode}
              programName={'HTF'}
              value={values.HTFAnnualRecertMonth}
            />
          </SetupGridItem>

          <PropertyStateConfig
            editMode={editMode}
            onChange={onChange}
            state={state}
            stateConfigs={stateConfigs}
            affordableProgramName={'HTF'}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <SetupLabel {...messagesAffordableSetup.hotmaActive} />
              <OnOffTypography on={hotmaConfig?.hasAdoptedHOTMA ?? false}>
                {hotmaConfig?.hasAdoptedHOTMA ?? false ? (
                  <FormattedMessage {...messagesAffordableSetup.on} />
                ) : (
                  <FormattedMessage {...messagesAffordableSetup.off} />
                )}
              </OnOffTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SetupLabel
                {...messagesAffordableSetup.hotmaImplementationDate}
              />
              <SetupValue
                name="HTF.HOTMAEffectiveDate"
                value={
                  hotmaConfig?.hasAdoptedHOTMA ?? false
                    ? hotmaConfig?.effectiveDate
                    : '---'
                }
                editable={false}
              />
            </Grid>
            <CertificationPastDue
              editMode={editMode}
              value={values.certificationPastDueDefinition}
              name="setup.HTFCertificationPastDueDefinition"
              onChange={onChange}
            />
          </Grid>
        </Grid>

        {flags.passbookRate && (
          <ProgramPassbookRate
            key={'HTF_PassbookRates'}
            programName={'HTF'}
            isLoadingPassbookRateData={isLoadingPassbookRateData}
            passbookRates={passbookRates}
            sx={{ paddingTop: 1, paddingRight: 0, paddingLeft: 0 }}
          />
        )}
      </Form>
    </>
  );
};

export default HTFSetup;
